/**
 * CommentItem - Single comment with reply support
 */
import { CheckIcon, ChatBubbleLeftIcon } from '@heroicons/react/24/outline';
import React, { useState } from 'react';

import {
  Comment,
  COMMENT_TYPE_REQUEST_CHANGES,
} from '../../types/review';
import { logger } from '../../utils/logger';

interface CommentItemProps {
  comment: Comment;
  isReviewer: boolean;
  isOwner?: boolean;
  canReply: boolean;
  currentReviewerId?: string | null;
  depth?: number;
  hideResolveButton?: boolean;
  onEditComment: (commentId: string, newText: string) => Promise<void>;
  onDeleteComment: (commentId: string) => Promise<void>;
  onReplyComment: (parentId: string, replyText: string) => Promise<void>;
  onToggleResolve: (commentId: string, currentStatus: string) => Promise<void>;
}

export const CommentItem: React.FC<CommentItemProps> = ({
  comment,
  isReviewer,
  isOwner = false,
  canReply,
  currentReviewerId,
  depth = 0,
  hideResolveButton = false,
  onEditComment,
  onDeleteComment,
  onReplyComment,
  onToggleResolve,
}) => {
  const [isReplying, setIsReplying] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isRequestChangesComment =
    comment.comment_type === COMMENT_TYPE_REQUEST_CHANGES && depth === 0;
  // Comments authored by an approver are visually distinguished from regular
  // reviewer comments.
  const isApproverComment = comment.reviewer_role === 'approver';
  // Only the participant who authored the comment may resolve or reopen it.
  // `hideResolveButton` is set once the review is approved, which disables
  // resolution for everyone.
  const hasResolvePermission = isReviewer || isOwner;
  const isCommentOpen = !hideResolveButton && comment.status === 'open';
  const canResolveOwnComment =
    currentReviewerId === comment.reviewer_id
    || (isOwner && !isRequestChangesComment);
  const canResolveComment =
    hasResolvePermission && isCommentOpen && canResolveOwnComment;

  const handleReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!replyText.trim() || isSubmitting) {return;}

    setIsSubmitting(true);
    try {
      await onReplyComment(comment.id, replyText);
      setReplyText('');
      setIsReplying(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStartEdit = () => {
    setEditingCommentId(comment.id);
    setEditText(comment.comment);
  };

  const handleSaveEdit = async () => {
    if (!editText.trim()) {return;}
    
    try {
      await onEditComment(comment.id, editText.trim());
      setEditingCommentId(null);
      setEditText('');
    } catch (error) {
      logger.error('Failed to save edit:', error);
    }
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditText('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  const marginLeft = depth * 24;

  return (
    <div className="relative" style={{ marginLeft: `${marginLeft}px` }}>
      {/* Thread connector line */}
      {depth > 0 && (
        <div className="absolute left-[-16px] top-0 bottom-0 w-0.5 bg-gray-200" />
      )}

      <div
        className={`group card shadow-sm border hover:shadow-md transition-all duration-200 ${
          isApproverComment
            ? 'bg-indigo-50 border-indigo-300 ring-1 ring-indigo-200'
            : 'bg-white border-gray-200'
        }`}
      >
        <div className="card-body p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              <div className="avatar placeholder">
                <div className="bg-neutral text-neutral-content rounded-full w-8">
                  <span className="text-xs">
                    {comment.reviewer_name
                      .split(' ')
                      .map((n) => n[0])
                      .join('')}
                  </span>
                </div>
              </div>
              <div>
                <p className="font-semibold text-sm flex items-center gap-1.5">
                  {comment.reviewer_name}
                  {isApproverComment && (
                    <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-indigo-100 text-indigo-700 rounded">
                      Approver
                    </span>
                  )}
                </p>
                <p className="text-xs text-base-content/60">
                  {new Date(comment.created_at).toLocaleString()}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {isRequestChangesComment && (
                <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
                  Requested Change
                </span>
              )}
              {comment.status === 'resolved' ? (
                // Resolved: show the same green check used by Archie's "Keep".
                <span
                  aria-label="Resolved"
                  title={
                    comment.resolved_by_name
                      ? `Resolved by ${comment.resolved_by_name}`
                      : 'Resolved'
                  }
                  className="inline-flex items-center justify-center rounded-md border border-[#008557] bg-[#008557] p-1.5 text-white"
                >
                  <CheckIcon className="w-4 h-4" />
                </span>
              ) : (
                isReviewer &&
                canResolveComment && (
                  <button
                    onClick={() => onToggleResolve(comment.id, comment.status)}
                    className="text-xs font-medium text-[#00614a] transition-colors hover:text-[#008557] hover:underline"
                  >
                    Mark as Resolved
                  </button>
                )
              )}
            </div>
          </div>

          {editingCommentId === comment.id ? (
            <div className="mt-2">
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:border-[#008557] focus:ring-2 focus:ring-[#008557]/20 focus:outline-none resize-none"
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={handleSaveEdit}
                rows={3}
              />
              <p className="text-xs text-gray-500 mt-1">Press Enter to save, Esc to cancel</p>
            </div>
          ) : (
            <>
              <p 
                className="mt-2 text-sm cursor-text hover:bg-gray-50 rounded p-1 -m-1"
                onDoubleClick={handleStartEdit}
                title="Double-click to edit"
              >
                {comment.comment}
              </p>
              {comment.status === 'resolved' && comment.resolved_at && (
                <p className="mt-2 text-xs text-green-700">
                  Resolved by {comment.resolved_by_name ?? 'Unknown user'} at{' '}
                  {new Date(comment.resolved_at).toLocaleString()}
                </p>
              )}

              {/* Reply Button */}
              {canReply && !isReplying && depth === 0 && (
                <button
                  onClick={() => setIsReplying(true)}
                  className="mt-2 text-xs text-gray-600 hover:text-[#008557] flex items-center gap-1"
                >
                  <ChatBubbleLeftIcon className="w-3 h-3" />
                  Reply
                </button>
              )}
            </>
          )}

          {/* Reply Form */}
          {isReplying && (
            <form onSubmit={handleReply} className="mt-3 bg-gray-50 border border-gray-200 rounded-lg p-3">
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:border-[#008557] focus:ring-2 focus:ring-[#008557]/20 focus:outline-none resize-none"
                placeholder="Write a reply..."
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                rows={2}
              />
              <div className="flex gap-2 justify-end mt-2">
                <button
                  type="button"
                  className="px-3 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-md"
                  onClick={() => {
                    setIsReplying(false);
                    setReplyText('');
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-3 py-1 text-xs font-medium text-white bg-[#008557] hover:bg-[#006b45] rounded-md disabled:bg-gray-300"
                  disabled={!replyText.trim() || isSubmitting}
                >
                  {isSubmitting ? 'Posting...' : 'Reply'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Render replies (only one level deep) */}
      {comment.replies && comment.replies.length > 0 && depth === 0 && (
        <div className="mt-3 space-y-3">
          {comment.replies.map((reply) => (
            <CommentItem
              key={reply.id}
              comment={reply}
              isReviewer={isReviewer}
              canReply={canReply}
              currentReviewerId={currentReviewerId}
              depth={depth + 1}
              hideResolveButton={hideResolveButton}
              onEditComment={onEditComment}
              onDeleteComment={onDeleteComment}
              onReplyComment={onReplyComment}
              onToggleResolve={onToggleResolve}
            />
          ))}
        </div>
      )}
    </div>
  );
};
